// Quick-list flow: photograph → upload → AI verdict → eBay listing in ~10s.
// Designed for items already purchased — skips the buy/skip step but reuses
// the same verdict/listing pipeline for accuracy parity with /scan.
//
// Four stages, all in this single component:
//   1. UPLOAD   — pick photos, compress in-browser, parallel-upload eagerly
//   2. ANALYZING — POST /api/quicklist/analyze with the storage paths,
//                  show a phased progress bar
//   3. LISTING   — EbayListingModal reused as-is
//   4. SUCCESS   — "Listed!" + "List Another Item" → back to stage 1
//
// Replaces the /bulk-upload entry point on the Scan page.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Camera, CheckCircle, Plus, Spinner, Storefront,
  Trash, UploadSimple, Warning, X,
} from "@phosphor-icons/react";
import { quicklistAPI } from "@/lib/api";
import { compressImage } from "@/lib/imageCompression";
import EbayListingModal from "@/components/app/EbayListingModal";
import BottomNav from "@/components/app/BottomNav";

const STAGE = {
  UPLOAD: "upload",
  ANALYZING: "analyzing",
  LISTING: "listing",
  SUCCESS: "success",
};

// Animated progress phases — labels match the backend job phases. We use
// the server-reported `phase` field to drive the UI bar instead of guessing
// based on elapsed time.
const PHASE_INFO = {
  queued: { label: "Queued", index: 0 },
  downloading: { label: "Reading uploaded photos", index: 0 },
  analyzing: { label: "Analyzing photos with AI", index: 1 },
  researching: { label: "Researching market prices", index: 2 },
  finalizing: { label: "Building eBay listing", index: 3 },
  ready: { label: "Ready", index: 4 },
  error: { label: "Error", index: -1 },
};
const PHASE_LABELS = ["Analyzing photos with AI", "Researching market prices", "Building eBay listing"];

export default function QuickListPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Stage state
  const [stage, setStage] = useState(STAGE.UPLOAD);

  // Stage 1 — uploaded photos. Each entry tracks its lifecycle locally so the
  // user sees red border + retry on per-file failure without nuking the others.
  // shape: { localId, file, previewUrl, status: "compressing"|"uploading"|"done"|"error",
  //          storage_path?, error?, progress: 0..100 }
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");

  // Stage 2 — analyzing. Server-driven progress via polling.
  const [jobId, setJobId] = useState(null);
  const [jobPhase, setJobPhase] = useState("queued");
  const [jobProgress, setJobProgress] = useState(0);
  const pollTimerRef = useRef(null);

  // Stage 3 — listing modal
  const [verdict, setVerdict] = useState(null);

  // Stage 4 — success payload from EbayListingModal
  const [listed, setListed] = useState(null);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      photos.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const successfulPaths = useMemo(
    () => photos.filter((p) => p.status === "done" && p.storage_path).map((p) => p.storage_path),
    [photos],
  );
  const anyInFlight = useMemo(
    () => photos.some((p) => p.status === "uploading" || p.status === "compressing"),
    [photos],
  );
  const anyError = useMemo(() => photos.some((p) => p.status === "error"), [photos]);

  // ---- Stage 1: upload ----
  const startUpload = async (localId, file) => {
    try {
      // 1) Compress client-side. iPhone HEIC → JPEG ~400KB.
      const compressed = await compressImage(file);
      setPhotos((prev) =>
        prev.map((p) => (p.localId === localId ? { ...p, status: "uploading", progress: 5 } : p)),
      );

      // 2) Upload
      const { data } = await quicklistAPI.upload(compressed, (e) => {
        if (!e?.total) return;
        const pct = Math.min(95, Math.round((e.loaded / e.total) * 90) + 5);
        setPhotos((prev) =>
          prev.map((p) => (p.localId === localId ? { ...p, progress: pct } : p)),
        );
      });

      setPhotos((prev) =>
        prev.map((p) =>
          p.localId === localId
            ? { ...p, status: "done", progress: 100, storage_path: data.storage_path }
            : p,
        ),
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      setPhotos((prev) =>
        prev.map((p) =>
          p.localId === localId ? { ...p, status: "error", error: detail, progress: 0 } : p,
        ),
      );
    }
  };

  const onFiles = (fileList) => {
    if (!fileList?.length) return;
    const files = Array.from(fileList).slice(0, 24);
    const added = files.map((file) => ({
      localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
      status: "compressing",
      progress: 0,
    }));
    setPhotos((prev) => [...prev, ...added].slice(0, 24));
    setError("");
    // Fire 3 parallel uploads at a time so an 8-photo batch finishes in ~5s on
    // good wifi without OOMing the K8s pod (each upload peaks ~4MB on backend).
    runWithConcurrency(added, 3, (p) => startUpload(p.localId, p.file));
  };

  const retryUpload = (p) => {
    setPhotos((prev) =>
      prev.map((x) => (x.localId === p.localId ? { ...x, status: "compressing", progress: 0, error: undefined } : x)),
    );
    startUpload(p.localId, p.file);
  };

  const removePhoto = (p) => {
    if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    setPhotos((prev) => prev.filter((x) => x.localId !== p.localId));
  };

  // ---- Stage 2: analyze (async job + polling) ----
  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const pollOnce = async (id) => {
    try {
      const { data } = await quicklistAPI.job(id);
      setJobPhase(data.phase || "queued");
      setJobProgress(typeof data.progress === "number" ? data.progress : 0);

      if (data.status === "ready" && data.verdict) {
        stopPolling();
        setVerdict(data.verdict);
        setStage(STAGE.LISTING);
        return;
      }
      if (data.status === "error" || data.phase === "error") {
        stopPolling();
        setError(data.error || "Analysis failed. Try again.");
        setStage(STAGE.UPLOAD);
        return;
      }
      // Keep polling — 1.5s cadence so we don't hammer the API
      pollTimerRef.current = setTimeout(() => pollOnce(id), 1500);
    } catch (err) {
      const status = err?.response?.status;
      // 404 → job is gone (TTL'd, lost). Bail out.
      if (status === 404) {
        stopPolling();
        setError("Job expired. Tap List Item to try again.");
        setStage(STAGE.UPLOAD);
        return;
      }
      // Transient network error — keep trying for ~2 minutes total
      pollTimerRef.current = setTimeout(() => pollOnce(id), 2000);
    }
  };

  const startAnalyze = async () => {
    if (anyInFlight) {
      setError("Wait for all photos to finish uploading.");
      return;
    }
    if (anyError) {
      setError("Retry or remove failed photos before listing.");
      return;
    }
    if (successfulPaths.length === 0) {
      setError("Add at least one photo.");
      return;
    }
    setError("");
    setJobPhase("queued");
    setJobProgress(0);
    setStage(STAGE.ANALYZING);

    try {
      const { data } = await quicklistAPI.analyze(successfulPaths, "");
      setJobId(data.job_id);
      // Kick off the poll loop
      pollOnce(data.job_id);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Could not start analysis";
      setError(detail);
      setStage(STAGE.UPLOAD);
    }
  };

  // Stop polling on unmount or stage change away from ANALYZING
  useEffect(() => {
    if (stage !== STAGE.ANALYZING) stopPolling();
    return stopPolling;
  }, [stage]);

  // ---- Stage 4: success → reset ----
  const listAnother = () => {
    photos.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    setPhotos([]);
    setVerdict(null);
    setListed(null);
    setError("");
    setStage(STAGE.UPLOAD);
  };

  // ---- Render ----
  return (
    <div className="min-h-screen bg-neutral-50 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b-4 border-black py-4 px-5 flex items-center gap-3">
        <button
          onClick={() => navigate("/scan")}
          className="p-1.5 border-2 border-black hover:bg-neutral-100 active:translate-y-[1px]"
          data-testid="quicklist-back"
        >
          <ArrowLeft size={18} weight="bold" />
        </button>
        <h1 className="font-heading text-xl font-black uppercase tracking-tight flex-1">
          Quick List
        </h1>
        {stage === STAGE.UPLOAD && (
          <div className="font-heading text-[10px] font-bold uppercase tracking-wide text-neutral-500">
            {successfulPaths.length}/{photos.length || 0} ready
          </div>
        )}
      </div>

      {/* STAGE 1: UPLOAD */}
      {stage === STAGE.UPLOAD && (
        <div className="p-5 max-w-2xl mx-auto space-y-5" data-testid="quicklist-upload-stage">
          {photos.length === 0 && (
            <div className="border-4 border-dashed border-neutral-300 p-8 text-center space-y-3 bg-white">
              <UploadSimple size={36} weight="bold" className="mx-auto text-neutral-400" />
              <div>
                <div className="font-heading text-lg font-black uppercase tracking-tight">
                  Photograph your item
                </div>
                <div className="font-body text-sm text-neutral-600 mt-1">
                  Add 4-8 clear photos. We'll handle the rest.
                </div>
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-black text-white py-3 font-heading text-sm font-bold uppercase tracking-widest border-2 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-y-[1px] active:translate-x-[1px] active:shadow-none transition-all flex items-center justify-center gap-2"
                data-testid="quicklist-pick-photos"
              >
                <Camera size={18} weight="bold" /> Add Photos
              </button>
            </div>
          )}

          {photos.length > 0 && (
            <>
              <div className="grid grid-cols-3 gap-2" data-testid="quicklist-photo-grid">
                {photos.map((p) => (
                  <PhotoTile
                    key={p.localId}
                    photo={p}
                    onRetry={() => retryUpload(p)}
                    onRemove={() => removePhoto(p)}
                  />
                ))}
                {photos.length < 24 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square border-4 border-dashed border-neutral-300 bg-white flex items-center justify-center font-heading text-xs font-bold uppercase tracking-wide text-neutral-500 hover:bg-neutral-100 active:translate-y-[1px]"
                    data-testid="quicklist-add-more"
                  >
                    <Plus size={20} weight="bold" />
                  </button>
                )}
              </div>

              {error && (
                <div className="border-2 border-red-500 bg-red-50 p-3 font-body text-sm text-red-700 flex items-start gap-2" data-testid="quicklist-error">
                  <Warning size={16} weight="bold" className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full bg-white text-black py-3 border-2 border-black font-heading text-sm font-bold uppercase tracking-widest active:bg-neutral-100"
                  data-testid="quicklist-upload-more"
                >
                  Upload More
                </button>
                <button
                  onClick={startAnalyze}
                  disabled={anyInFlight || anyError || successfulPaths.length === 0}
                  className="w-full bg-[#00E676] text-black py-3 border-2 border-black font-heading text-sm font-bold uppercase tracking-widest shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-y-[1px] active:translate-x-[1px] active:shadow-none transition-all disabled:bg-neutral-200 disabled:text-neutral-400 disabled:shadow-none disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="quicklist-list-item"
                >
                  <Storefront size={16} weight="bold" /> List Item
                </button>
              </div>
            </>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              onFiles(e.target.files);
              e.target.value = "";
            }}
            data-testid="quicklist-file-input"
          />
        </div>
      )}

      {/* STAGE 2: ANALYZING */}
      {stage === STAGE.ANALYZING && (
        <AnalyzingPanel phase={jobPhase} progress={jobProgress} />
      )}

      {/* STAGE 3: LISTING — reuse EbayListingModal */}
      {stage === STAGE.LISTING && verdict && (
        <EbayListingModal
          verdict={verdict}
          onClose={() => setStage(STAGE.UPLOAD)}
          onSuccess={(data) => {
            setListed(data);
            setStage(STAGE.SUCCESS);
          }}
        />
      )}

      {/* STAGE 4: SUCCESS */}
      {stage === STAGE.SUCCESS && (
        <SuccessPanel listed={listed} onListAnother={listAnother} onDone={() => navigate("/history")} />
      )}

      <BottomNav />
    </div>
  );
}

// ----------------- Subcomponents -----------------

function PhotoTile({ photo, onRetry, onRemove }) {
  const isDone = photo.status === "done";
  const isError = photo.status === "error";
  const isUploading = photo.status === "uploading" || photo.status === "compressing";

  return (
    <div className="relative aspect-square border-2 border-black bg-white overflow-hidden" data-testid={`quicklist-photo-${photo.localId}`}>
      {photo.previewUrl && (
        <img
          src={photo.previewUrl}
          alt=""
          className={`w-full h-full object-cover ${isError ? "opacity-40" : ""}`}
        />
      )}
      {/* Overlay while uploading */}
      {isUploading && (
        <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white">
          <Spinner size={20} weight="bold" className="animate-spin" />
          <div className="font-heading text-[10px] font-bold uppercase tracking-wide mt-1">
            {photo.progress}%
          </div>
        </div>
      )}
      {/* Done check */}
      {isDone && (
        <div className="absolute top-1 right-1 bg-[#00E676] border-2 border-black p-0.5">
          <CheckCircle size={12} weight="bold" />
        </div>
      )}
      {/* Error state */}
      {isError && (
        <div className="absolute inset-0 bg-red-500/80 flex flex-col items-center justify-center text-white p-1">
          <Warning size={20} weight="bold" />
          <button
            onClick={onRetry}
            className="mt-1 font-heading text-[10px] font-bold uppercase tracking-wide bg-white text-black px-2 py-0.5 border-2 border-black"
            data-testid={`quicklist-retry-${photo.localId}`}
          >
            Retry
          </button>
        </div>
      )}
      {/* Always-on remove button */}
      <button
        onClick={onRemove}
        className="absolute top-1 left-1 bg-white border-2 border-black p-0.5 hover:bg-red-50"
        data-testid={`quicklist-remove-${photo.localId}`}
        title="Remove"
      >
        <Trash size={10} weight="bold" />
      </button>
    </div>
  );
}

function AnalyzingPanel({ phase, progress }) {
  const info = PHASE_INFO[phase] || PHASE_INFO.queued;
  const activeIdx = Math.max(0, Math.min(2, info.index));
  // Use a smoothed progress floor so the bar always moves forward even on
  // slow networks; cap at 95 until the server flips status=ready.
  const displayPct = Math.max(progress, Math.min(95, (activeIdx + 1) * 25));

  return (
    <div className="px-5 pt-10 max-w-md mx-auto space-y-6" data-testid="quicklist-analyzing">
      <div className="text-center space-y-2">
        <div className="inline-block bg-black text-white p-3 border-2 border-black">
          <Spinner size={28} weight="bold" className="animate-spin" />
        </div>
        <h2 className="font-heading text-xl font-black uppercase tracking-tight">
          Building your listing
        </h2>
        <p className="font-body text-sm text-neutral-600">
          This usually takes 5-15 seconds. Hang tight — we'll open the listing as soon as it's ready.
        </p>
      </div>

      <div className="space-y-3">
        <div className="h-3 bg-neutral-200 border-2 border-black overflow-hidden">
          <div
            className="h-full bg-[#00E676] transition-all duration-500 ease-out"
            style={{ width: `${displayPct}%` }}
            data-testid="quicklist-progress-bar"
          />
        </div>
        <div className="space-y-2">
          {PHASE_LABELS.map((label, i) => {
            const done = i < activeIdx || phase === "ready";
            const active = i === activeIdx && phase !== "ready";
            return (
              <div
                key={label}
                className={`flex items-center gap-2 ${done ? "text-neutral-500" : active ? "text-black" : "text-neutral-400"}`}
              >
                {done ? (
                  <CheckCircle size={16} weight="bold" className="text-green-600 shrink-0" />
                ) : active ? (
                  <Spinner size={16} weight="bold" className="animate-spin shrink-0" />
                ) : (
                  <div className="w-4 h-4 border-2 border-current shrink-0" />
                )}
                <span className={`font-body text-sm ${active ? "font-semibold" : ""}`}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SuccessPanel({ listed, onListAnother, onDone }) {
  return (
    <div className="px-5 pt-10 max-w-md mx-auto space-y-6" data-testid="quicklist-success">
      <div className="text-center space-y-3">
        <div className="inline-block bg-[#00E676] p-4 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <CheckCircle size={32} weight="bold" />
        </div>
        <h2 className="font-heading text-2xl font-black uppercase tracking-tight">
          Listed on eBay
        </h2>
        {listed?.listing_id && (
          <p className="font-body text-xs text-neutral-600">Item #{listed.listing_id}</p>
        )}
      </div>

      {listed?.ebay_url && (
        <a
          href={listed.ebay_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full text-center bg-white text-black py-3 border-2 border-black font-heading text-sm font-bold uppercase tracking-widest active:bg-neutral-100"
          data-testid="quicklist-view-on-ebay"
        >
          View on eBay
        </a>
      )}

      <button
        onClick={onListAnother}
        className="w-full bg-[#00E676] text-black py-4 border-2 border-black font-heading text-base font-black uppercase tracking-widest shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[1px] active:translate-x-[1px] active:shadow-none transition-all flex items-center justify-center gap-2"
        data-testid="quicklist-list-another"
      >
        <Plus size={18} weight="bold" /> List Another Item
      </button>
      <button
        onClick={onDone}
        className="w-full bg-white text-black py-3 border-2 border-black font-heading text-sm font-bold uppercase tracking-widest active:bg-neutral-100"
        data-testid="quicklist-done"
      >
        Done — View History
      </button>
    </div>
  );
}

// ----------------- Helpers -----------------

// Bounded-concurrency executor: starts `concurrency` tasks at once, queues the
// rest, and resolves when all are done. We don't care about return values.
function runWithConcurrency(items, concurrency, task) {
  let i = 0;
  const launch = async () => {
    while (i < items.length) {
      const idx = i++;
      await task(items[idx]);
    }
  };
  return Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, launch));
}
